import React, { useState, useCallback, useEffect } from 'react';
import {
    Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Button, Paper, TextField, Switch, FormControlLabel, Typography, CircularProgress,
    Select, MenuItem, FormControl, IconButton, Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useFirebase } from '../../context/FirebaseContext';
import {
    getAdminUsersV2, getAdminUserConfigV2, updateAdminUserConfigV2, updateAdminUserStatusV2,
    getSubscriptionTiersV2, setUserSubscriptionTierV2, getSubscriptionUsersV2, resetUserDailyUsageV2,
    deleteAdminUserV2, getAdminUsersUsageSummaryV2
} from '../../api/admin';
import logger from '../../utils/logger';
import './AdminView.css';

const UsersTab = ({ onViewLogs }) => {
    const { auth } = useFirebase();
    const [users, setUsers] = useState([]);
    const [userConfig, setUserConfig] = useState(null);
    const [loading, setLoading] = useState(false);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [tiers, setTiers] = useState([]);
    const [userTiers, setUserTiers] = useState({});
    const [usageSummary, setUsageSummary] = useState({});

    useEffect(() => {
        if (auth.currentUser) {
            setCurrentUserId(auth.currentUser.uid);
        }
    }, [auth.currentUser]);

    // Load subscription tiers on mount
    const loadTiers = useCallback(async () => {
        try {
            const response = await new Promise((resolve) => {
                getSubscriptionTiersV2(auth, resolve);
            });
            if (Array.isArray(response)) {
                setTiers(response);
            } else if (response.data) {
                setTiers(response.data);
            }
        } catch (error) {
            logger.error('Error loading tiers:', error);
        }
    }, [auth]);

    // Load user subscriptions to get current tier assignments
    const loadUserSubscriptions = useCallback(async () => {
        try {
            const response = await new Promise((resolve) => {
                getSubscriptionUsersV2(auth, resolve);
            });
            if (Array.isArray(response)) {
                const tierMap = {};
                response.forEach(sub => {
                    tierMap[sub.userId] = sub.tierId;
                });
                setUserTiers(tierMap);
            } else if (response.data) {
                const tierMap = {};
                response.data.forEach(sub => {
                    tierMap[sub.userId] = sub.tierId;
                });
                setUserTiers(tierMap);
            }
        } catch (error) {
            logger.error('Error loading user subscriptions:', error);
        }
    }, [auth]);

    // Load usage summary for all users
    const loadUsageSummary = useCallback(async () => {
        try {
            const response = await new Promise((resolve) => {
                getAdminUsersUsageSummaryV2(auth, resolve);
            });
            if (response && typeof response === 'object' && !response.error) {
                setUsageSummary(response);
            }
        } catch (error) {
            logger.error('Error loading usage summary:', error);
        }
    }, [auth]);

    useEffect(() => {
        loadTiers();
        loadUserSubscriptions();
        loadUsageSummary();
    }, [loadTiers, loadUserSubscriptions, loadUsageSummary]);

    const loadUsers = useCallback(async () => {
        try {
            setLoading(true);
            const response = await new Promise((resolve) => {
                getAdminUsersV2(auth, resolve);
            });
            if (Array.isArray(response)) {
                setUsers(response);
            } else if (response.data) {
                setUsers(response.data);
            } else if (response.error) {
                logger.error('Error from API:', response.error);
            }
        } catch (error) {
            logger.error('Error loading users:', error);
        } finally {
            setLoading(false);
        }
    }, [auth]);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    const loadUserConfig = async (userId) => {
        try {
            setLoading(true);
            const response = await new Promise((resolve) => {
                getAdminUserConfigV2(auth, resolve, userId);
            });
            if (response && typeof response === 'object' && !response.error) {
                setUserConfig(response);
            } else if (response.data) {
                setUserConfig(response.data);
            } else if (response.error) {
                logger.error('Error loading user config:', response.error);
            }
        } catch (error) {
            logger.error('Error loading user config:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveConfig = async () => {
        try {
            setLoading(true);
            const configToSave = {
                userId: userConfig.userId,
                dailyMessagesLimit: userConfig.dailyMessagesLimit,
                premium: userConfig.premium,
                premiumMessagesLimit: userConfig.premiumMessagesLimit,
                // Subscription limit overrides (empty string -> null)
                overrideDailyStaticValidations: userConfig.overrideDailyStaticValidations === '' ? null : userConfig.overrideDailyStaticValidations,
                overrideDailyAiValidations: userConfig.overrideDailyAiValidations === '' ? null : userConfig.overrideDailyAiValidations,
                overrideDailyWebhookCalls: userConfig.overrideDailyWebhookCalls === '' ? null : userConfig.overrideDailyWebhookCalls,
                overrideMaxStaticRules: userConfig.overrideMaxStaticRules === '' ? null : userConfig.overrideMaxStaticRules,
                overrideMaxAiRules: userConfig.overrideMaxAiRules === '' ? null : userConfig.overrideMaxAiRules,
                overrideMaxPipelines: userConfig.overrideMaxPipelines === '' ? null : userConfig.overrideMaxPipelines,
            };
            await new Promise((resolve) => {
                updateAdminUserConfigV2(auth, resolve, configToSave.userId, configToSave);
            });
            loadUsers();
            setUserConfig(null);
        } catch (error) {
            logger.error('Error saving user config:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleConfigChange = (field, value) => {
        setUserConfig(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleResetUsage = async (userId) => {
        const confirmed = window.confirm(
            'Are you sure you want to reset the daily usage for this user? This will allow them to send more messages today.'
        );
        if (!confirmed) return;

        try {
            setLoading(true);
            const response = await new Promise((resolve) => {
                resetUserDailyUsageV2(auth, resolve, userId);
            });
            if (response.error) {
                alert('Failed to reset usage: ' + response.error);
            } else {
                alert('Daily usage reset successfully!');
            }
        } catch (error) {
            logger.error('Error resetting usage:', error);
            alert('Failed to reset daily usage.');
        } finally {
            setLoading(false);
        }
    };

    const handleTierChange = async (userId, newTierId) => {
        try {
            setLoading(true);
            await new Promise((resolve) => {
                setUserSubscriptionTierV2(auth, resolve, userId, newTierId);
            });
            setUserTiers(prev => ({ ...prev, [userId]: newTierId }));
        } catch (error) {
            logger.error('Error updating user tier:', error);
            alert('Failed to update user subscription tier.');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (userId, field, value) => {
        try {
            const user = users.find(u => u.userId === userId);
            if (!user) return;

            if (field === 'admin') {
                const confirmed = window.confirm(
                    `Are you sure you want to ${value ? 'grant' : 'revoke'} admin privileges for ${user.name || user.email}?`
                );
                if (!confirmed) {
                    await loadUsers();
                    return;
                }
            }

            setLoading(true);
            const newStatus = {
                admin: field === 'admin' ? value : user.admin,
                active: field === 'active' ? value : user.active
            };

            const response = await new Promise((resolve) => {
                updateAdminUserStatusV2(auth, resolve, userId, newStatus);
            });

            if (response.error) {
                alert(response.error);
                await loadUsers();
            } else {
                await loadUsers();
            }
        } catch (error) {
            logger.error('Error updating user status:', error);
            alert('Failed to update user status. Please try again.');
            await loadUsers();
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteUser = async (userId, userName, userEmail) => {
        const confirmed = window.confirm(
            `⚠️ PERMANENT DELETION\n\n` +
            `Are you sure you want to permanently delete this user?\n\n` +
            `User: ${userName || 'No Name'}\n` +
            `Email: ${userEmail}\n\n` +
            `This will delete ALL user data including:\n` +
            `• Chat history and messages\n` +
            `• Trade configurations\n` +
            `• Validation history\n` +
            `• Subscription data\n\n` +
            `This action CANNOT be undone.`
        );
        if (!confirmed) return;

        try {
            setLoading(true);
            const response = await new Promise((resolve) => {
                deleteAdminUserV2(auth, resolve, userId);
            });
            if (response.error) {
                alert('Failed to delete user: ' + response.error);
            } else {
                alert('User deleted successfully.');
                await loadUsers();
            }
        } catch (error) {
            logger.error('Error deleting user:', error);
            alert('Failed to delete user. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const formatArrayDate = (dateArray) => {
        if (!dateArray || !Array.isArray(dateArray)) return 'N/A';
        const [year, month, day, hour, minute, second] = dateArray;
        return new Date(year, month - 1, day, hour, minute, second).toLocaleString();
    };

    if (loading && users.length === 0) {
        return (
            <Box className="admin-loading">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box className="admin-tab-content">
            {/* Desktop Table View */}
            <Box className="admin-table-desktop">
                <TableContainer component={Paper} className="admin-table-container">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>User</TableCell>
                                <TableCell>Subscription</TableCell>
                                <TableCell className="admin-hide-mobile">Usage Today</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell className="admin-hide-mobile">Last Login</TableCell>
                                <TableCell className="admin-hide-mobile">Created</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((user) => (
                                <TableRow key={user.userId} className="admin-table-row">
                                    <TableCell>
                                        <Box className="admin-user-cell">
                                            {user.picture && (
                                                <img
                                                    src={user.picture}
                                                    alt={user.name}
                                                    className="admin-user-avatar"
                                                />
                                            )}
                                            <Box className="admin-user-info">
                                                <span className="admin-user-name">{user.name || 'No Name'}</span>
                                                <span className="admin-user-email">{user.email}</span>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <FormControl size="small" sx={{ minWidth: 100 }}>
                                            <Select
                                                value={userTiers[user.userId] || 'free'}
                                                onChange={(e) => handleTierChange(user.userId, e.target.value)}
                                                displayEmpty
                                                className="admin-tier-select"
                                            >
                                                {tiers.map((tier) => (
                                                    <MenuItem key={tier.id} value={tier.id}>
                                                        {tier.name}
                                                    </MenuItem>
                                                ))}
                                                {tiers.length === 0 && (
                                                    <MenuItem value="free">Free</MenuItem>
                                                )}
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell className="admin-hide-mobile">
                                        {usageSummary[user.userId] ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, fontSize: '0.75rem' }}>
                                                <Tooltip title="Static validations today">
                                                    <span>V: {usageSummary[user.userId].staticValidations || 0}</span>
                                                </Tooltip>
                                                <Tooltip title="Webhook calls today">
                                                    <span>W: {usageSummary[user.userId].webhookCalls || 0}</span>
                                                </Tooltip>
                                            </Box>
                                        ) : (
                                            <span style={{ color: '#999', fontSize: '0.75rem' }}>—</span>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Box className="admin-status-cell">
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        size="small"
                                                        checked={user.active}
                                                        onChange={(e) => handleStatusChange(user.userId, 'active', e.target.checked)}
                                                    />
                                                }
                                                label="Active"
                                                className="admin-switch"
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        size="small"
                                                        checked={user.admin}
                                                        onChange={(e) => handleStatusChange(user.userId, 'admin', e.target.checked)}
                                                        disabled={user.userId === currentUserId}
                                                    />
                                                }
                                                label="Admin"
                                                className="admin-switch"
                                            />
                                            <span className="admin-verified-badge">
                                                {user.emailVerified ? '✓ Verified' : '✗ Unverified'}
                                            </span>
                                        </Box>
                                    </TableCell>
                                    <TableCell className="admin-hide-mobile">
                                        {formatArrayDate(user.lastLogin)}
                                    </TableCell>
                                    <TableCell className="admin-hide-mobile">
                                        {formatArrayDate(user.createdAt)}
                                    </TableCell>
                                    <TableCell>
                                        <Box className="admin-actions">
                                            <Button
                                                variant="outlined"
                                                onClick={() => loadUserConfig(user.userId)}
                                                size="small"
                                                className="admin-btn"
                                            >
                                                Config
                                            </Button>
                                            <Button
                                                variant="text"
                                                onClick={() => onViewLogs?.(user.userId)}
                                                size="small"
                                                className="admin-btn-secondary"
                                            >
                                                Logs
                                            </Button>
                                            <Tooltip title={user.userId === currentUserId ? "Cannot delete yourself" : "Delete user permanently"}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDeleteUser(user.userId, user.name, user.email)}
                                                        disabled={user.userId === currentUserId}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Mobile Card View */}
            <Box className="admin-cards-mobile">
                {users.map((user) => (
                    <Paper key={user.userId} className="admin-user-card">
                        <Box className="admin-user-card-header">
                            {user.picture && (
                                <img src={user.picture} alt={user.name} className="admin-user-avatar" />
                            )}
                            <Box className="admin-user-info">
                                <span className="admin-user-name">{user.name || 'No Name'}</span>
                                <span className="admin-user-email">{user.email}</span>
                            </Box>
                        </Box>
                        <Box className="admin-user-card-body">
                            <Box className="admin-status-row">
                                <FormControlLabel
                                    control={
                                        <Switch
                                            size="small"
                                            checked={user.active}
                                            onChange={(e) => handleStatusChange(user.userId, 'active', e.target.checked)}
                                        />
                                    }
                                    label="Active"
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            size="small"
                                            checked={user.admin}
                                            onChange={(e) => handleStatusChange(user.userId, 'admin', e.target.checked)}
                                            disabled={user.userId === currentUserId}
                                        />
                                    }
                                    label="Admin"
                                />
                            </Box>
                            <Box className="admin-user-card-meta">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Typography variant="body2" color="textSecondary">Tier:</Typography>
                                    <FormControl size="small" sx={{ minWidth: 100 }}>
                                        <Select
                                            value={userTiers[user.userId] || 'free'}
                                            onChange={(e) => handleTierChange(user.userId, e.target.value)}
                                            displayEmpty
                                        >
                                            {tiers.map((tier) => (
                                                <MenuItem key={tier.id} value={tier.id}>
                                                    {tier.name}
                                                </MenuItem>
                                            ))}
                                            {tiers.length === 0 && (
                                                <MenuItem value="free">Free</MenuItem>
                                            )}
                                        </Select>
                                    </FormControl>
                                </Box>
                                {usageSummary[user.userId] && (
                                    <Box sx={{ display: 'flex', gap: 2, mb: 0.5, fontSize: '0.75rem' }}>
                                        <span>Validations: {usageSummary[user.userId].staticValidations || 0}</span>
                                        <span>Webhooks: {usageSummary[user.userId].webhookCalls || 0}</span>
                                    </Box>
                                )}
                                <span>Last: {formatArrayDate(user.lastLogin)}</span>
                            </Box>
                        </Box>
                        <Box className="admin-user-card-actions">
                            <Button
                                variant="outlined"
                                onClick={() => loadUserConfig(user.userId)}
                                size="small"
                                fullWidth
                            >
                                View Config
                            </Button>
                            <Button
                                variant="text"
                                onClick={() => onViewLogs?.(user.userId)}
                                size="small"
                                fullWidth
                            >
                                View Logs
                            </Button>
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={() => handleDeleteUser(user.userId, user.name, user.email)}
                                size="small"
                                fullWidth
                                disabled={user.userId === currentUserId}
                                startIcon={<DeleteIcon />}
                            >
                                Delete
                            </Button>
                        </Box>
                    </Paper>
                ))}
            </Box>

            {/* User Config Panel */}
            {userConfig && (
                <Paper className="admin-config-panel">
                    <Typography variant="h6" className="admin-config-title">
                        User Configuration
                    </Typography>
                    <Box className="admin-config-form">
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={userConfig.premium}
                                    onChange={(e) => handleConfigChange('premium', e.target.checked)}
                                />
                            }
                            label="Premium User"
                        />
                        <TextField
                            label="Daily Messages Limit"
                            type="number"
                            value={userConfig.dailyMessagesLimit}
                            onChange={(e) => handleConfigChange('dailyMessagesLimit', parseInt(e.target.value))}
                            fullWidth
                            size="small"
                        />
                        <TextField
                            label="Premium Messages Limit"
                            type="number"
                            value={userConfig.premiumMessagesLimit}
                            onChange={(e) => handleConfigChange('premiumMessagesLimit', parseInt(e.target.value))}
                            fullWidth
                            size="small"
                        />

                        {/* Subscription Limit Overrides */}
                        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: 'text.secondary' }}>
                            Subscription Limit Overrides (empty = use tier default, -1 = unlimited)
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                            <TextField
                                label="Daily Validations"
                                type="number"
                                value={userConfig.overrideDailyStaticValidations ?? ''}
                                onChange={(e) => handleConfigChange('overrideDailyStaticValidations', e.target.value === '' ? null : parseInt(e.target.value))}
                                size="small"
                                placeholder="Tier default"
                            />
                            <TextField
                                label="Daily AI Validations"
                                type="number"
                                value={userConfig.overrideDailyAiValidations ?? ''}
                                onChange={(e) => handleConfigChange('overrideDailyAiValidations', e.target.value === '' ? null : parseInt(e.target.value))}
                                size="small"
                                placeholder="Tier default"
                            />
                            <TextField
                                label="Daily Webhook Calls"
                                type="number"
                                value={userConfig.overrideDailyWebhookCalls ?? ''}
                                onChange={(e) => handleConfigChange('overrideDailyWebhookCalls', e.target.value === '' ? null : parseInt(e.target.value))}
                                size="small"
                                placeholder="Tier default"
                            />
                            <TextField
                                label="Max Pipelines"
                                type="number"
                                value={userConfig.overrideMaxPipelines ?? ''}
                                onChange={(e) => handleConfigChange('overrideMaxPipelines', e.target.value === '' ? null : parseInt(e.target.value))}
                                size="small"
                                placeholder="Tier default"
                            />
                            <TextField
                                label="Max Static Rules"
                                type="number"
                                value={userConfig.overrideMaxStaticRules ?? ''}
                                onChange={(e) => handleConfigChange('overrideMaxStaticRules', e.target.value === '' ? null : parseInt(e.target.value))}
                                size="small"
                                placeholder="Tier default"
                            />
                            <TextField
                                label="Max AI Rules"
                                type="number"
                                value={userConfig.overrideMaxAiRules ?? ''}
                                onChange={(e) => handleConfigChange('overrideMaxAiRules', e.target.value === '' ? null : parseInt(e.target.value))}
                                size="small"
                                placeholder="Tier default"
                            />
                        </Box>

                        <Box className="admin-config-actions">
                            <Button
                                variant="outlined"
                                color="warning"
                                onClick={() => handleResetUsage(userConfig.userId)}
                                sx={{ mr: 1 }}
                            >
                                Reset Usage
                            </Button>
                            <Button variant="text" onClick={() => setUserConfig(null)}>
                                Cancel
                            </Button>
                            <Button variant="contained" onClick={handleSaveConfig}>
                                Save
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            )}
        </Box>
    );
};

export default UsersTab;
